import { watch } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';

import { i18n } from '@/i18n';
import { useAuthStore } from '@/stores/auth';

const TITLE_KEYS: Record<string, string> = {
  login: 'routes.login',
  cases: 'routes.cases',
  'case-new': 'routes.caseNew',
  'case-processing': 'routes.processing',
  'case-facts': 'routes.facts',
  'case-costs': 'routes.costs',
  'case-report': 'routes.report',
  'case-audit': 'routes.audit',
};

function applyTitle(name: string | symbol | undefined | null): void {
  const key = typeof name === 'string' ? TITLE_KEYS[name] : undefined;
  const title = key ? String(i18n.global.t(key)) : undefined;
  document.title = title ? `${title} · Wildschadenhilfe` : 'Wildschadenhilfe';
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      component: () => import('@/layouts/AppLayout.vue'),
      children: [
        {
          path: '',
          name: 'cases',
          component: () => import('@/views/CaseListView.vue'),
        },
        {
          path: 'cases/new',
          name: 'case-new',
          component: () => import('@/views/NewCaseView.vue'),
        },
        {
          path: 'cases/:id',
          component: () => import('@/views/case/CaseShell.vue'),
          props: true,
          children: [
            {
              path: '',
              name: 'case-processing',
              component: () => import('@/views/case/ProcessingTab.vue'),
            },
            {
              path: 'facts',
              name: 'case-facts',
              component: () => import('@/views/case/FactsTab.vue'),
            },
            {
              path: 'costs',
              name: 'case-costs',
              component: () => import('@/views/case/CostsTab.vue'),
            },
            {
              path: 'report',
              name: 'case-report',
              component: () => import('@/views/case/ReportTab.vue'),
            },
            {
              path: 'audit',
              name: 'case-audit',
              component: () => import('@/views/case/AuditTab.vue'),
            },
          ],
        },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  await auth.restore();

  if (!to.meta.public && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: 'cases' };
  }
  return true;
});

router.afterEach((to) => {
  applyTitle(to.name);
});

watch(
  () => i18n.global.locale.value,
  () => applyTitle(router.currentRoute.value.name),
);

export default router;
